import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  Alert,
  Paper,
  Tooltip,
} from '@mui/material';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import { CoordinateGrid } from '../coordinateGrid';
import {
  parseTrigEquation,
  generateTrigGraphVariations,
  trigGraphElements,
  defaultTrigGraphConfig,
  normalizeStep1Types,
  suggestXMin,
  suggestXMax,
  suggestYMin,
  suggestYMax,
  trigEquationToLatex,
  formatPiValue,
  computeAsymptotesInOnePeriod,
  STEP1_TYPES_BY_FUNC,
  STEP1_TYPE_LABELS,
} from './trigGraphUtils';

/**
 * Admin component for creating/editing trig graph questions
 *
 * Props:
 * - config: TrigGraphConfig - Current configuration
 * - onConfigChange: (config) => void - Called when config changes
 * - correctAnswer: { correctGraphIndex: number } - Correct answer
 * - onAnswerChange: (answer) => void - Called when answer changes
 */
const TrigGraphAdmin = ({
  config = defaultTrigGraphConfig,
  onConfigChange,
  correctAnswer,
  onAnswerChange,
}) => {
  const [equationInput, setEquationInput] = useState(config.equation || '');
  const [parseError, setParseError] = useState('');
  const [variations, setVariations] = useState([]);
  const [gridError, setGridError] = useState('');

  const step1Types = normalizeStep1Types(config);

  // Keep latest callbacks in refs so effects always call the current version
  const onConfigChangeRef = useRef(onConfigChange);
  onConfigChangeRef.current = onConfigChange;
  const onAnswerChangeRef = useRef(onAnswerChange);
  onAnswerChangeRef.current = onAnswerChange;

  // Get available step1 types based on function type
  const availableStep1Types = useMemo(() => {
    if (!config.parsedTrig) return ['period', 'phaseShift', 'verticalShift'];
    return STEP1_TYPES_BY_FUNC[config.parsedTrig.funcType] || ['period'];
  }, [config.parsedTrig]);

  // STATE RESTORATION + AUTO-PARSE on mount
  useEffect(() => {
    if (!config.equation) return;

    if (config.parsedTrig && config.variationSeed) {
      // Existing config — just regenerate variations
      setEquationInput(config.equation);
      try {
        const types = normalizeStep1Types(config);
        const { variations: vars } = generateTrigGraphVariations(
          config.parsedTrig,
          types,
          config.variationSeed
        );
        setVariations(vars);
        setParseError('');
      } catch (e) {
        setParseError('Failed to restore question configuration');
      }
    } else {
      // Equation present but missing parsed data or seed — auto-parse
      setEquationInput(config.equation);
      try {
        const parsed = parseTrigEquation(config.equation);
        const types = normalizeStep1Types(config);
        const seed = Date.now();
        const { variations: vars, correctIndex } = generateTrigGraphVariations(
          parsed, types, seed
        );
        setVariations(vars);
        setParseError('');
        onConfigChangeRef.current({
          ...config,
          equation: config.equation,
          parsedTrig: parsed,
          step1Types: types,
          variationSeed: seed,
          correctGraphIndex: correctIndex,
        });
        onAnswerChangeRef.current({ correctGraphIndex: correctIndex });
      } catch (e) {
        setParseError(e.message);
      }
    }
  }, []); // Only run on mount

  // Parse equation when input changes
  const handleEquationChange = useCallback((value) => {
    setEquationInput(value);

    if (!value.trim()) {
      setParseError('');
      setVariations([]);
      return;
    }

    try {
      const parsed = parseTrigEquation(value);
      setParseError('');

      // Ensure step1Types are valid for this function type
      const validTypes = STEP1_TYPES_BY_FUNC[parsed.funcType] || ['period'];
      let currentTypes = step1Types.filter(t => validTypes.includes(t));
      if (currentTypes.length === 0) currentTypes = [validTypes[0]];

      const seed = Date.now();
      const { variations: vars, correctIndex } = generateTrigGraphVariations(
        parsed, currentTypes, seed
      );
      setVariations(vars);

      onConfigChange({
        ...config,
        equation: value,
        parsedTrig: parsed,
        step1Types: currentTypes,
        variationSeed: seed,
        correctGraphIndex: correctIndex,
      });

      onAnswerChange({ correctGraphIndex: correctIndex });
    } catch (e) {
      setParseError(e.message);
      setVariations([]);
    }
  }, [config, step1Types, onConfigChange, onAnswerChange]);

  // Regenerate variations when step1 type checkboxes change
  const handleStep1TypeToggle = useCallback((typeKey) => {
    let newTypes;
    if (step1Types.includes(typeKey)) {
      newTypes = step1Types.filter(t => t !== typeKey);
      if (newTypes.length === 0) return;
    } else {
      if (step1Types.length >= 2) return;
      newTypes = [...step1Types, typeKey];
    }

    if (config.parsedTrig) {
      const seed = Date.now();
      const { variations: vars, correctIndex } = generateTrigGraphVariations(
        config.parsedTrig, newTypes, seed
      );
      setVariations(vars);

      onConfigChange({
        ...config,
        step1Types: newTypes,
        variationSeed: seed,
        correctGraphIndex: correctIndex,
      });

      onAnswerChange({ correctGraphIndex: correctIndex });
    } else {
      onConfigChange({ ...config, step1Types: newTypes });
    }
  }, [config, step1Types, onConfigChange, onAnswerChange]);

  // Handle grid config changes with validation
  const handleGridChange = useCallback((key, value) => {
    const newGrid = { ...(config.grid || defaultTrigGraphConfig.grid), [key]: value };

    if (newGrid.xMin >= newGrid.xMax) {
      setGridError('X Min must be less than X Max');
    } else if (newGrid.yMin >= newGrid.yMax) {
      setGridError('Y Min must be less than Y Max');
    } else {
      setGridError('');
    }

    onConfigChange({ ...config, grid: newGrid });
  }, [config, onConfigChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Section 1: Equation Input */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          1. Enter Trig Function
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the trig function. Examples:
          <br />
          - y = 2sin(3x)
          <br />
          - f(x) = -3cos(pix/2 - pi/4) + 1
          <br />
          - y = tan(2x - pi/3)
          <br />
          - f(x) = 2csc(x) - 1
        </Typography>

        <TextField
          fullWidth
          label="Trig Function"
          value={equationInput}
          onChange={(e) => handleEquationChange(e.target.value)}
          error={!!parseError}
          helperText={parseError || 'Use pi for π, ^ for exponents. e.g., y = 2tan(3x)'}
          placeholder="y = 2sin(3x)"
        />

        {/* LaTeX Preview */}
        {equationInput && !parseError && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Preview:
            </Typography>
            <Box sx={{ fontSize: '1.2rem', mt: 1 }}>
              <span dangerouslySetInnerHTML={{
                __html: katex.renderToString(trigEquationToLatex(equationInput), { throwOnError: false })
              }} />
            </Box>
          </Box>
        )}
      </Paper>

      {/* Section 2: Step 1 Type Selection */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          2. Choose Step 1 Type(s)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Select 1 or 2 types for students to identify in Step 1.
        </Typography>
        <FormControl component="fieldset">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(STEP1_TYPE_LABELS).map(([key, label]) => {
              const isAvailable = availableStep1Types.includes(key);
              return (
                <Tooltip
                  key={key}
                  title={!isAvailable ? `Not applicable for ${config.parsedTrig?.funcType || 'this'} functions` : ''}
                >
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={step1Types.includes(key)}
                          onChange={() => handleStep1TypeToggle(key)}
                          disabled={!isAvailable}
                        />
                      }
                      label={label}
                    />
                  </span>
                </Tooltip>
              );
            })}
          </Box>
        </FormControl>

        {step1Types.length >= 2 && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
            Maximum 2 types selected.
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Students will find the {step1Types.map(t => STEP1_TYPE_LABELS[t]?.toLowerCase() || t).join(' and ')}.
        </Typography>
      </Paper>

      {/* Section 3: Parsed Information */}
      {config.parsedTrig && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            3. Parsed Trig Function Information
          </Typography>
          <ParsedTrigSummary parsedTrig={config.parsedTrig} />
        </Paper>
      )}

      {/* Section 4: Grid Configuration */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          4. Grid Configuration
        </Typography>

        {config.parsedTrig && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Suggested grid bounds:</strong> Based on your function's period and amplitude,
              consider setting:
              <br />
              X range: [{suggestXMin(config.parsedTrig)}, {suggestXMax(config.parsedTrig)}]
              <br />
              Y range: [{suggestYMin(config.parsedTrig)}, {suggestYMax(config.parsedTrig)}]
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <TextField
            type="number"
            label="X Min"
            value={config.grid?.xMin ?? -7}
            onChange={(e) => handleGridChange('xMin', Number(e.target.value))}
          />
          <TextField
            type="number"
            label="X Max"
            value={config.grid?.xMax ?? 7}
            onChange={(e) => handleGridChange('xMax', Number(e.target.value))}
          />
          <TextField
            type="number"
            label="Y Min"
            value={config.grid?.yMin ?? -5}
            onChange={(e) => handleGridChange('yMin', Number(e.target.value))}
          />
          <TextField
            type="number"
            label="Y Max"
            value={config.grid?.yMax ?? 5}
            onChange={(e) => handleGridChange('yMax', Number(e.target.value))}
          />
        </Box>

        {gridError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {gridError}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Tip: Ensure the grid shows at least one full period of the function.
        </Typography>
      </Paper>

      {/* Section 5: Graph Preview */}
      {variations.length === 4 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            5. Generated Graph Variations
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Graph {variations.find(v => v.isCorrect)?.label} is the correct answer.
            All graphs share the same {step1Types.map(t => STEP1_TYPE_LABELS[t]?.toLowerCase() || t).join(' and ')}.
          </Alert>
          <TrigGraphPreview
            variations={variations}
            gridConfig={config.grid}
          />
        </Paper>
      )}
    </Box>
  );
};

/**
 * Display parsed trig function information
 */
const ParsedTrigSummary = ({ parsedTrig }) => {
  const {
    funcType,
    A,
    B,
    C,
    D,
    amplitude,
    period,
    phaseShift,
    yIntercept,
  } = parsedTrig;

  const isSinCos = funcType === 'sin' || funcType === 'cos';
  const asymptotes = !isSinCos ? computeAsymptotesInOnePeriod(parsedTrig) : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography>
        <strong>Function Type:</strong> {funcType}
      </Typography>
      <Typography>
        <strong>A (coefficient):</strong> {A} | <strong>B (frequency):</strong> {formatPiValue(B)} | <strong>C (phase constant):</strong> {formatPiValue(C)} | <strong>D (vertical shift):</strong> {D}
      </Typography>
      {isSinCos && (
        <Typography>
          <strong>Amplitude:</strong> {amplitude}
        </Typography>
      )}
      <Typography>
        <strong>Period:</strong> {formatPiValue(period)}
      </Typography>
      <Typography>
        <strong>Phase Shift:</strong> {formatPiValue(phaseShift)}
      </Typography>
      <Typography>
        <strong>Vertical Shift:</strong> {D}
      </Typography>
      <Typography>
        <strong>Y-intercept:</strong> {yIntercept !== undefined ? `(0, ${yIntercept.toFixed(4)})` : 'undefined (asymptote at x=0)'}
      </Typography>
      {asymptotes.length > 0 && (
        <>
          <Typography><strong>Vertical Asymptotes (one period):</strong></Typography>
          <Box sx={{ pl: 2 }}>
            {asymptotes.map((a, i) => (
              <Typography key={i}>x = {formatPiValue(a)}</Typography>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

/**
 * Preview all 4 graph variations in a 2x2 grid
 */
const TrigGraphPreview = ({ variations, gridConfig }) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 2,
      }}
    >
      {variations.map((variation) => (
        <Paper
          key={variation.label}
          sx={{
            p: 1,
            border: variation.isCorrect ? '3px solid green' : '1px solid grey',
            bgcolor: variation.isCorrect ? 'success.light' : 'background.paper',
          }}
        >
          <Typography variant="subtitle2" align="center">
            Graph {variation.label}
            {variation.isCorrect && ' (Correct)'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CoordinateGrid
              config={gridConfig}
              elements={trigGraphElements(variation.trig, gridConfig)}
              pendingPoints={[]}
              mode="point"
              readOnly={true}
              width={200}
              height={200}
            />
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default TrigGraphAdmin;
